import { useState } from "react";
import { TYPO } from "styles/Typo";
import { usePortfolioPrice } from "utils/clientAPIs/portfolio";
import Image from "next/image";
import { useHandleImageErrorVer2 } from "utils/useHandleImageError";
import {
  PortfolioDailyGain,
  PortfolioTotalPrice,
  PortfolioTotalProfit,
  PortfolioTotalProfitPercent,
} from "utils/portfolio";
import { useRouter } from "next/router";
import Button from "components/common/buttons/buttons";
import SkeletonBlock, {
  SkeletonBlock2,
  SkeletonBlock3,
} from "components/skeleton";

function PortfolioTotalAmount({ portId }: { portId: number }) {
  const router = useRouter();
  const [toggleOpen, setToggleOpen] = useState(true);
  const { getStockLogoSafe, onError } = useHandleImageErrorVer2();
  const { data, isLoading, isError } = usePortfolioPrice(portId);
  const price = data?.price;
  const items = data?.items;
  if (isLoading)
    return (
      <div className="px-4">
        <SkeletonBlock3 width={"100%"} height={104} />
      </div>
    );
  if (isLoading || isError || items.length === 0) return null;
  return (
    <div className="px-4">
      <div className="bg-1 rounded-[16px] ">
        <div
          className={`px-4 pt-3 pb-2 flex items-center justify-between  ${
            price !== null ? "cursor-pointer" : ""
          }`}
          onClick={() => {
            if (price !== null) {
              setToggleOpen(!toggleOpen);
            }
          }}
        >
          <div className="">
            <p className={`${TYPO.TYPO8_RG} text-gray60`}>총 자산</p>
            <p
              className={`${
                true ? "cursor-pointer text-blue40 flex gap-1 items-center" : ""
              } ${TYPO.TYPO5_1_SB} ${
                price === null ? "text-blue40 cursor-pointer" : "text-gray80"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (price === null) {
                  router.push(`/portfolio/${portId}/setting`);
                }
              }}
              data-gtm-id="pf-main-amount-edit-init-btn"
            >
              {price === null ? (
                "수량/평단 입력하기"
              ) : (
                <>{PortfolioTotalPrice(price, "ko")}</>
              )}
              {price === null && <EditIcon />}
            </p>
          </div>
          <Button
            text="포트폴리오 편집"
            size="sm"
            type="tertiary"
            clickHandler={() => {
              router.push(`/portfolio/${portId}/edit`);
            }}
            dataGtmId="pf-main-amount-edit-btn"
          />
        </div>
        <div>
          <div className="px-4 flex flex-col gap-1">
            <div className="flex justify-between items-center">
              <p className={`${TYPO.TYPO7_1_RG} text-gray60`}>보유 종목</p>
              <div className="flex gap-1">
                <div
                  className="flex "
                  style={{
                    transform: `translateX(${Math.min(items.length, 2) * 7}px)`,
                  }}
                >
                  {items?.slice(0, 3).map((item: any, index: number) => (
                    <Image
                      key={index}
                      src={getStockLogoSafe(item?.ITEM_CD_DL || item?.TICKER)}
                      alt={item?.ITEM_CD_DL || item?.TICKER}
                      width={20}
                      height={20}
                      className="rounded-full"
                      onError={() => onError(item?.ITEM_CD_DL || item?.TICKER)}
                      style={{
                        transform:
                          index > 0 ? `translateX(-${index * 7}px)` : "none",
                        zIndex: items.length - index,
                      }}
                    />
                  ))}
                </div>
                {items.length > 3 && (
                  <p
                    className={`${TYPO.TYPO8_RG} text-gray60 px-1 bg-gray5 rounded-[6px]`}
                  >
                    +{items.length - 3}
                  </p>
                )}
              </div>
            </div>
            {price !== null && (
              <>
                <div className="flex justify-between items-center">
                  <p className={`${TYPO.TYPO7_1_RG} text-gray60`}>총 수익</p>
                  <p
                    className={`${TYPO.TYPO7_1_RG}  ${
                      Number(price?.totalChange.toFixed(2)) === 0
                        ? "text-gray60"
                        : Number(price?.totalChange.toFixed(2)) > 0
                        ? "text-red40"
                        : "text-blue50"
                    }`}
                  >
                    {Number(
                      (
                        price?.totalPortfolioPriceKr - price?.totalUserPriceKr
                      ).toFixed(0)
                    ) > 0
                      ? "+"
                      : Number(
                          (
                            price?.totalPortfolioPriceKr -
                            price?.totalUserPriceKr
                          ).toFixed(0)
                        ) === 0
                      ? ""
                      : "-"}
                    {PortfolioTotalProfit(price, "ko")}{" "}
                    {PortfolioTotalProfitPercent(price)}{" "}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p className={`${TYPO.TYPO7_1_RG} text-gray60`}>일간 수익</p>
                  <p
                    className={`${TYPO.TYPO7_1_RG} ${
                      Number((price?.totalTodayChangePer * 100).toFixed(2)) > 0
                        ? "text-red50"
                        : Number(
                            (price?.totalTodayChangePer * 100).toFixed(2)
                          ) === 0
                        ? "text-gray60"
                        : "text-blue50"
                    }`}
                  >
                    {price?.totalTodayProfit > 0
                      ? "+"
                      : price?.totalTodayProfit === 0
                      ? ""
                      : "-"}
                    {PortfolioDailyGain(price, "ko")}{" "}
                    {`(${Math.abs(
                      price.totalTodayChangePer * 100
                    ).toLocaleString("en-us", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    })}%)`}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="h-4" />
      </div>
    </div>
  );
}

const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
  >
    <path
      d="M2.10274 10.4867C1.92774 10.5284 1.77566 10.4846 1.64649 10.3555C1.51733 10.2263 1.47358 10.0742 1.51524 9.89922L1.95274 7.77422L4.22774 10.0492L2.10274 10.4867ZM5.04024 9.43672L2.56524 6.96172L7.72775 1.79922C7.91941 1.60755 8.15691 1.51172 8.44024 1.51172C8.72358 1.51172 8.96108 1.60755 9.15274 1.79922L10.2027 2.84922C10.3944 3.04089 10.4902 3.27839 10.4902 3.56172C10.4902 3.84505 10.3944 4.08255 10.2027 4.27422L5.04024 9.43672Z"
      fill="var(--blue40)"
    />
  </svg>
);

export default PortfolioTotalAmount;
